/**
 * useEntitlements Hook
 *
 * Permission checking hook based on entitlements from user roles.
 * User → Roles → Entitlements
 *
 * Entitlements are returned as an array of IDs from the login endpoint.
 * Each entitlement controls access to specific business operations.
 *
 * Usage Examples:
 * ```tsx
 * // Check specific entitlement
 * const canCreate = useEntitlement("User.Create");
 *
 * // Check if user has ANY entitlement for an entity (to show menu)
 * const hasUserAccess = useEntitlementEntity("User");
 *
 * // Check if user has ALL of multiple entitlements
 * const canEditUser = useEntitlements(["User.Read", "User.Update"]);
 *
 * // Check if user has ANY of multiple entitlements
 * const canViewDashboard = useEntitlementsAny(["Case.Read", "Document.Read"]);
 * ```
 */

import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  ENTITLEMENT_ID_TO_NAME,
  ENTITLEMENT_ENTITIES,
  type EntityName,
  type EntitlementName,
} from "../constants/entitlements";

/**
 * Check if user has a specific entitlement
 *
 * @param entitlementName - Entitlement name (e.g., "User.Create", "DocumentType.Read")
 * @returns boolean - true if user has the entitlement
 *
 * @example
 * ```tsx
 * const CreateUserButton = () => {
 *   const canCreate = useEntitlement("User.Create");
 *
 *   if (!canCreate) return null;
 *
 *   return <Button onClick={handleCreate}>Create User</Button>;
 * };
 * ```
 */
export const useEntitlement = (
  entitlementName: EntitlementName | string,
): boolean => {
  const { entitlements, user } = useAuth();

  return useMemo(() => {
    // Super admin has all permissions
    if (user?.role?.toLowerCase() === "superadmin") {
      return true;
    }

    // Check if user has the entitlement by name
    return entitlements.includes(entitlementName);
  }, [entitlements, entitlementName, user?.role]);
};

/**
 * Check if user has ALL of the specified entitlements
 *
 * @param entitlementNames - Array of entitlement names
 * @returns boolean - true if user has ALL entitlements
 *
 * @example
 * ```tsx
 * const EditUserDialog = () => {
 *   const canEdit = useEntitlements(["User.Read", "User.Update"]);
 *
 *   if (!canEdit) return <AccessDenied />;
 *
 *   return <Dialog>...</Dialog>;
 * };
 * ```
 */
export const useEntitlements = (
  entitlementNames: (EntitlementName | string)[],
): boolean => {
  const { entitlements, user } = useAuth();

  return useMemo(() => {
    // Super admin has all permissions
    if (user?.role?.toLowerCase() === "superadmin") {
      return true;
    }

    // Check if user has ALL the entitlements
    return entitlementNames.every((name) => entitlements.includes(name));
  }, [entitlements, entitlementNames, user?.role]);
};

/**
 * Check if user has ANY of the specified entitlements
 *
 * @param entitlementNames - Array of entitlement names
 * @returns boolean - true if user has ANY entitlement
 *
 * @example
 * ```tsx
 * const ReportsPage = () => {
 *   const hasAccess = useEntitlementsAny(["Case.Read", "Document.Read"]);
 *
 *   if (!hasAccess) return <AccessDenied />;
 *
 *   return <Dashboard />;
 * };
 * ```
 */
export const useEntitlementsAny = (
  entitlementNames: (EntitlementName | string)[],
): boolean => {
  const { entitlements, user } = useAuth();

  return useMemo(() => {
    // Super admin has all permissions
    if (user?.role?.toLowerCase() === "superadmin") {
      return true;
    }

    // Check if user has ANY of the entitlements
    return entitlementNames.some((name) => entitlements.includes(name));
  }, [entitlements, entitlementNames, user?.role]);
};

/**
 * Check if user has ANY entitlement for a specific entity
 * Useful for showing/hiding menu items based on module access
 *
 * @param entityName - Entity name (e.g., "User", "DocumentType", "Case")
 * @returns boolean - true if user has ANY entitlement for the entity
 *
 * @example
 * ```tsx
 * const NavigationMenu = () => {
 *   const showDocumentTypes = useEntitlementEntity("DocumentType");
 *   const showUsers = useEntitlementEntity("User");
 *
 *   return (
 *     <Menu>
 *       {showUsers && <MenuItem to="/users">Users</MenuItem>}
 *       {showDocumentTypes && <MenuItem to="/document-types">Document Types</MenuItem>}
 *     </Menu>
 *   );
 * };
 * ```
 */
export const useEntitlementEntity = (
  entityName: EntityName | string,
): boolean => {
  const { entitlementIds, user } = useAuth();

  return useMemo(() => {
    // Super admin has all permissions
    if (user?.role?.toLowerCase() === "superadmin") {
      return true;
    }

    // Get the entity's entitlement IDs
    const entityIds = ENTITLEMENT_ENTITIES[entityName as EntityName];
    if (!entityIds) {
      console.warn(`Unknown entity: ${entityName}`);
      return false;
    }

    // Check if user has ANY entitlement ID for this entity
    return entityIds.some((id) => entitlementIds.includes(id));
  }, [entitlementIds, entityName, user?.role]);
};

/**
 * Get all entitlements the current user has (as names)
 *
 * @returns Array of entitlement names
 *
 * @example
 * ```tsx
 * const DebugPanel = () => {
 *   const entitlements = useUserEntitlements();
 *
 *   return (
 *     <div>
 *       <h3>Your Permissions:</h3>
 *       <ul>
 *         {entitlements.map(ent => <li key={ent}>{ent}</li>)}
 *       </ul>
 *     </div>
 *   );
 * };
 * ```
 */
export const useUserEntitlements = (): string[] => {
  const { entitlements } = useAuth();
  return entitlements;
};

/**
 * Get all entitlement IDs the current user has
 *
 * @returns Array of entitlement IDs
 */
export const useUserEntitlementIds = (): number[] => {
  const { entitlementIds } = useAuth();
  return entitlementIds;
};

/**
 * Permission check utilities
 */
export const EntitlementUtils = {
  /**
   * Create a permission checker function
   */
  createChecker: (entitlementIds: number[], userRole?: string) => {
    const isSuperAdmin = userRole?.toLowerCase() === "superadmin";

    // Convert IDs to names for string-based checks
    const entitlementNames = entitlementIds
      .map((id) => ENTITLEMENT_ID_TO_NAME[id])
      .filter(Boolean);

    return {
      /**
       * Check if user has a specific entitlement by name
       */
      has: (name: string) => isSuperAdmin || entitlementNames.includes(name),

      /**
       * Check if user has ALL of the specified entitlements
       */
      hasAll: (names: string[]) =>
        isSuperAdmin || names.every((name) => entitlementNames.includes(name)),

      /**
       * Check if user has ANY of the specified entitlements
       */
      hasAny: (names: string[]) =>
        isSuperAdmin || names.some((name) => entitlementNames.includes(name)),

      /**
       * Check if user has ANY entitlement for an entity
       */
      hasEntity: (entityName: string) => {
        if (isSuperAdmin) return true;
        const entityIds = ENTITLEMENT_ENTITIES[entityName as EntityName];
        return entityIds
          ? entityIds.some((id) => entitlementIds.includes(id))
          : false;
      },

      isSuperAdmin,
      entitlementNames,
      entitlementIds,
    };
  },
};

export default useEntitlement;
